import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Input, Select, Trash } from '@react-apps/design-system';
import {
  EXAM_KIND_LABELS,
  EXAM_TYPE_LABELS,
  FIELD_TITLE_CLASS,
  getApiErrorMessage,
  notifyFailed,
} from '../display';
import {
  EXAM_KINDS,
  EXAM_TYPES,
  type ContentType,
  type ExamKind,
  type ExamType,
  type QuestionBody,
  type QuestionImageInput,
} from '../../domain/entities/content.entity';
import { RichTextEditor, type PickedImage } from './RichTextEditor';
import { QuestionBodyView } from './QuestionBodyView';
import { QuestionFlagBadges, questionFlags } from './QuestionFlagBadges';
import { Switch } from './Switch';
import { ImportIssueCard } from './ImportIssueCard';
import { DismissibleHint } from './DismissibleHint';
import { IconButton } from '@/shared/components/icon-button';
import { InstitutionSelect } from './InstitutionSelect';
import {
  attachedFilenames,
  convertIssue,
  editorHtmlToTokens,
  openImageFilePicker,
  preflightChosenFile,
  preparedToBase64,
  richTextIsEmpty,
  sanitizeRichText,
  toDataUri,
  toImagePayload,
  tokensToEditorHtml,
  type AttachedImage,
  type PreflightIssue,
  type PreparedImage,
} from '@react-apps/content-editor';
import { describeIssue } from '../image-preflight-messages';

/**
 * O que um sub-form entrega ao dono: o `body` pronto e, para `QUESTION`, as
 * imagens novas a subir junto (o endpoint recebe as duas coisas num payload só).
 */
export interface BodyFormResult {
  body: unknown;
  images?: QuestionImageInput[];
}

/**
 * `build()` = valida e monta o resultado, ou `null` deixando o erro local na
 * tela. Exposto num ref para o dono poder salvar **sem** depender do clique no
 * botão do form — é o que faz "Salvar e continuar" da guarda de edição usar
 * exatamente o mesmo caminho do botão Salvar, em vez de uma segunda validação
 * que pode divergir.
 */
export type BodyFormBuildRef = React.MutableRefObject<
  (() => BodyFormResult | null) | null
>;

/**
 * Editor de `body` específico por tipo de conteúdo. Substitui a edição de JSON
 * cru por campos tipados — evita erros de sintaxe/estrutura. Cada sub-form
 * inicializa a partir do body atual, valida no submit e entrega um body pronto
 * (no formato que o `ContentTypeHandler` do back espera).
 */
export function BodyForm({
  type,
  body,
  pending,
  error,
  onSubmit,
  onCancel,
  onDirtyChange,
  buildRef,
  imageUrlsByFileId,
  annulled,
  extraSections,
}: {
  type: ContentType;
  body: unknown;
  pending: boolean;
  error: unknown;
  onSubmit: (result: BodyFormResult) => void;
  onCancel: () => void;
  /** Avisa o dono quando o form deixa de refletir o que foi carregado. */
  onDirtyChange?: (dirty: boolean) => void;
  buildRef?: BodyFormBuildRef;
  /** `QUESTION`: URLs das imagens já salvas, para miniatura e Preview. */
  imageUrlsByFileId?: Record<string, string>;
  /** `QUESTION`: "anulada" atual — semeia o interruptor das Marcações. */
  annulled?: boolean;
  /**
   * `QUESTION`: seções extras renderizadas **dentro do `<form>`**, logo antes
   * do Salvar. É por onde a classificação e o comentário oficial entram no card
   * sem empurrar o botão para o meio da tela.
   */
  extraSections?: ReactNode;
}) {
  const shared = {
    body,
    pending,
    error,
    onSubmit,
    onCancel,
    onDirtyChange,
    buildRef,
  };
  switch (type) {
    case 'FLASHCARD':
      return <FlashcardForm {...shared} />;
    case 'OFFICIAL_COMMENT':
      return (
        <OfficialCommentForm
          {...shared}
          imageUrlsByFileId={imageUrlsByFileId ?? {}}
        />
      );
    case 'VIDEO':
      return <VideoForm {...shared} />;
    case 'EXAM':
      return <ExamForm {...shared} />;
    case 'QUESTION':
      return (
        <QuestionForm
          {...shared}
          imageUrlsByFileId={imageUrlsByFileId ?? {}}
          annulled={annulled ?? false}
          extraSections={extraSections}
        />
      );
    default:
      return null;
  }
}

// --- Infra compartilhada -----------------------------------------------------

interface SubFormProps {
  body: unknown;
  pending: boolean;
  error: unknown;
  onSubmit: (result: BodyFormResult) => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  buildRef?: BodyFormBuildRef;
}

/**
 * Liga um sub-form ao dono: reporta "tem alteração não salva" e publica o
 * `build()` no `buildRef`. Devolve o `onSubmit` do `<form>`.
 *
 * Sujo é comparação de **valor** (snapshot atual ≠ snapshot do mount), não
 * "algum `onChange` disparou": voltar o texto ao que era destrava a troca de
 * questão, e o TipTap emite `onUpdate` em situações que não são edição — o que
 * faria a guarda avisar sobre alteração que não existe.
 */
function useFormBridge({
  snapshot,
  build,
  onSubmit,
  onDirtyChange,
  buildRef,
}: {
  snapshot: string;
  build: () => BodyFormResult | null;
  onSubmit: (result: BodyFormResult) => void;
  onDirtyChange?: (dirty: boolean) => void;
  buildRef?: BodyFormBuildRef;
}) {
  const initial = useRef(snapshot);
  const dirty = snapshot !== initial.current;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // `build` fecha sobre o estado do render atual; o ref publicado tem de chamar
  // sempre a versão mais nova, senão "Salvar e continuar" salvaria o form como
  // ele estava no primeiro render.
  const latest = useRef(build);
  useEffect(() => {
    latest.current = build;
  });
  useEffect(() => {
    if (!buildRef) return;
    buildRef.current = () => latest.current();
    return () => {
      buildRef.current = null;
    };
  }, [buildRef]);

  return {
    /** `onSubmit` do `<form>`. */
    handleSubmit: (e: React.FormEvent) => {
      e.preventDefault();
      const result = build();
      if (result) {
        onSubmit(result);
        return;
      }
      // `build()` recusou e já pôs o motivo exato no formulário — mas isso é
      // uma linha vermelha que, num card longo (enunciado + 5 alternativas +
      // classificação + comentário), pode estar fora da área visível de quem
      // acabou de clicar. Sem aviso nenhum, o clique em Salvar fica
      // indistinguível de um salvamento que deu certo: nada muda na tela, e a
      // pessoa sai achando que salvou. O toast diz QUE não salvou; o motivo
      // continua sendo o inline.
      notifyFailed(
        null,
        'Nada foi salvo — confira o campo marcado em vermelho',
      );
    },
    /** O form deixou de refletir o que foi carregado. */
    dirty,
  };
}

function asRecord(body: unknown): Record<string, unknown> {
  return typeof body === 'object' && body !== null
    ? (body as Record<string, unknown>)
    : {};
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function num(value: unknown): string {
  return typeof value === 'number' ? String(value) : '';
}

/** '' → undefined; número inválido → erro. */
function parseOptionalNumber(
  s: string,
): { ok: true; value: number | undefined } | { ok: false } {
  const t = s.trim();
  if (t === '') return { ok: true, value: undefined };
  const n = Number(t);
  if (!Number.isFinite(n)) return { ok: false };
  return { ok: true, value: n };
}

function FormActions({
  pending,
  localError,
  serverError,
  onCancel,
  onRestore,
}: {
  pending: boolean;
  localError: string | null;
  serverError: unknown;
  onCancel: () => void;
  /** Volta o form ao que foi carregado. Ausente = o form não tem o que restaurar. */
  onRestore?: () => void;
}) {
  return (
    <>
      {(localError || serverError != null) && (
        <p className="text-sm text-red-600" role="alert">
          {localError ?? getApiErrorMessage(serverError, 'Falha ao salvar')}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={pending}>
          Salvar
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={pending}
        >
          Cancelar
        </Button>
        {onRestore && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRestore}
            disabled={pending}
            title="Descarta o que foi editado e volta ao texto que estava salvo"
          >
            Restaurar original
          </Button>
        )}
      </div>
    </>
  );
}

/** Textarea rotulada — o design system não expõe um <Textarea>. */
function TextareaField({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <label className="block space-y-1.5">
      <span className={FIELD_TITLE_CLASS}>{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) w-full rounded-lg border p-3 text-sm"
      />
    </label>
  );
}

// --- FLASHCARD ---------------------------------------------------------------

function FlashcardForm({
  body,
  pending,
  error,
  onSubmit,
  onCancel,
  onDirtyChange,
  buildRef,
}: SubFormProps) {
  const b = asRecord(body);
  const [front, setFront] = useState(str(b.front));
  const [back, setBack] = useState(str(b.back));
  const [localError, setLocalError] = useState<string | null>(null);

  const build = (): BodyFormResult | null => {
    if (richTextIsEmpty(front) || richTextIsEmpty(back)) {
      setLocalError('Frente e verso são obrigatórios.');
      return null;
    }
    setLocalError(null);
    return {
      body: {
        front: sanitizeRichText(front),
        back: sanitizeRichText(back),
      },
    };
  };

  const { handleSubmit } = useFormBridge({
    snapshot: JSON.stringify({ front, back }),
    build,
    onSubmit,
    onDirtyChange,
    buildRef,
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <RichTextEditor label="Frente" value={front} onChange={setFront} />
      <RichTextEditor label="Verso" value={back} onChange={setBack} />
      <FormActions
        pending={pending}
        localError={localError}
        serverError={error}
        onCancel={onCancel}
      />
    </form>
  );
}

// --- OFFICIAL_COMMENT --------------------------------------------------------

/**
 * O texto entra com as imagens já salvas como nó do editor e sai de volta como
 * token — sem a conversão, o `{{img:<fileId>}}` aparecia como texto solto e
 * apagá-lo sem querer tirava a imagem do comentário. Imagem **nova** se anexa
 * no card da questão, na página da prova (`OfficialCommentField`).
 */
function OfficialCommentForm({
  body,
  imageUrlsByFileId,
  pending,
  error,
  onSubmit,
  onCancel,
  onDirtyChange,
  buildRef,
}: SubFormProps & { imageUrlsByFileId: Record<string, string> }) {
  const b = asRecord(body);
  const [text, setText] = useState(() =>
    tokensToEditorHtml(str(b.text), imageUrlsByFileId),
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const build = (): BodyFormResult | null => {
    // Token antes da checagem: comentário só de imagem é texto válido.
    const tokens = editorHtmlToTokens(text);
    if (richTextIsEmpty(tokens)) {
      setLocalError('O texto é obrigatório.');
      return null;
    }
    setLocalError(null);
    return { body: { text: sanitizeRichText(tokens) } };
  };

  const { handleSubmit } = useFormBridge({
    snapshot: JSON.stringify({ text }),
    build,
    onSubmit,
    onDirtyChange,
    buildRef,
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <RichTextEditor label="Texto" value={text} onChange={setText} />
      <FormActions
        pending={pending}
        localError={localError}
        serverError={error}
        onCancel={onCancel}
      />
    </form>
  );
}

// --- VIDEO -------------------------------------------------------------------

function VideoForm({
  body,
  pending,
  error,
  onSubmit,
  onCancel,
  onDirtyChange,
  buildRef,
}: SubFormProps) {
  const b = asRecord(body);
  const provider = asRecord(b.provider);
  const [url, setUrl] = useState(str(b.url));
  const [durationSec, setDurationSec] = useState(num(b.durationSec));
  const [providerName, setProviderName] = useState(str(provider.name));
  const [providerExternalId, setProviderExternalId] = useState(
    str(provider.externalId),
  );
  const [transcript, setTranscript] = useState(str(b.transcript));
  const [localError, setLocalError] = useState<string | null>(null);

  const build = (): BodyFormResult | null => {
    if (url.trim() === '') {
      setLocalError('A URL é obrigatória.');
      return null;
    }
    const duration = parseOptionalNumber(durationSec);
    if (!duration.ok) {
      setLocalError('Duração (s) deve ser um número.');
      return null;
    }
    const hasName = providerName.trim() !== '';
    const hasExternalId = providerExternalId.trim() !== '';
    if (hasName !== hasExternalId) {
      setLocalError(
        'Provedor: preencha nome e ID externo juntos, ou deixe ambos vazios.',
      );
      return null;
    }
    setLocalError(null);

    const payload: Record<string, unknown> = { url: url.trim() };
    if (hasName && hasExternalId) {
      payload.provider = {
        name: providerName.trim(),
        externalId: providerExternalId.trim(),
      };
    }
    if (duration.value !== undefined) payload.durationSec = duration.value;
    if (transcript.trim() !== '') payload.transcript = transcript.trim();
    return { body: payload };
  };

  const { handleSubmit } = useFormBridge({
    snapshot: JSON.stringify({
      url,
      durationSec,
      providerName,
      providerExternalId,
      transcript,
    }),
    build,
    onSubmit,
    onDirtyChange,
    buildRef,
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="URL"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://…"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Duração (s)"
          type="number"
          value={durationSec}
          onChange={(e) => setDurationSec(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Provedor — nome (opcional)"
          value={providerName}
          onChange={(e) => setProviderName(e.target.value)}
          placeholder="ex.: youtube"
        />
        <Input
          label="Provedor — ID externo (opcional)"
          value={providerExternalId}
          onChange={(e) => setProviderExternalId(e.target.value)}
        />
      </div>
      <TextareaField
        label="Transcrição (opcional)"
        value={transcript}
        onChange={setTranscript}
        rows={4}
      />
      <FormActions
        pending={pending}
        localError={localError}
        serverError={error}
        onCancel={onCancel}
      />
    </form>
  );
}

// --- EXAM --------------------------------------------------------------------

/**
 * Lê um campo de union do body cru, caindo no valor que a migração de backfill
 * gravou quando o campo ainda não existe na linha. Não é palpite: é o mesmo
 * valor que o banco já tem — e o `select` precisa começar em alguma opção
 * válida, senão o navegador escolhe a primeira por conta própria e a edição
 * reescreve o campo sem ninguém ter tocado nele.
 */
function pickOption<T extends string>(
  allowed: readonly T[],
  raw: unknown,
  fallback: T,
): T {
  return allowed.includes(raw as T) ? (raw as T) : fallback;
}

function ExamForm({
  body,
  pending,
  error,
  onSubmit,
  onCancel,
  onDirtyChange,
  buildRef,
}: SubFormProps) {
  const b = asRecord(body);
  const [year, setYear] = useState(num(b.year));
  const [institutionId, setInstitutionId] = useState(str(b.institutionId));
  const [variant, setVariant] = useState(str(b.variant));
  const [examType, setExamType] = useState<ExamType>(
    pickOption(EXAM_TYPES, b.examType, 'DIRECT_ACCESS'),
  );
  const [examKind, setExamKind] = useState<ExamKind>(
    pickOption(EXAM_KINDS, b.examKind, 'REAL_EXAM'),
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const build = (): BodyFormResult | null => {
    const parsedYear = parseOptionalNumber(year);
    if (!parsedYear.ok || parsedYear.value === undefined) {
      setLocalError('Ano é obrigatório e deve ser um número.');
      return null;
    }
    if (institutionId.trim() === '' || variant.trim() === '') {
      setLocalError('Instituição e variante são obrigatórias.');
      return null;
    }
    setLocalError(null);
    return {
      body: {
        year: parsedYear.value,
        institutionId: institutionId.trim(),
        variant: variant.trim(),
        examType,
        examKind,
      },
    };
  };

  const { handleSubmit } = useFormBridge({
    snapshot: JSON.stringify({
      year,
      institutionId,
      variant,
      examType,
      examKind,
    }),
    build,
    onSubmit,
    onDirtyChange,
    buildRef,
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-(--color-text-secondary) text-xs">
        Todos os campos são obrigatórios; as questões da prova vêm das relações
        (EXAM CONTÉM QUESTION).
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Input
          label="Ano"
          type="number"
          value={year}
          onChange={(e) => setYear(e.target.value)}
        />
        {/* UF + instituição, no lugar do UUID digitado à mão. */}
        <InstitutionSelect value={institutionId} onChange={setInstitutionId} />
        <Input
          label="Variante"
          value={variant}
          onChange={(e) => setVariant(e.target.value)}
        />
        {/* Os dois eixos precisam estar aqui, e não só no modal de criação: o
            `body` é substituído inteiro no save, então campo ausente do form é
            campo que o handler do back recusa como faltando. Rótulos iguais
            aos do `CreateContentItemModal` — trilha da residência de um lado,
            real × simulado do outro. */}
        <Select
          label="Tipo da prova"
          value={examType}
          onChange={(e) => setExamType(e.target.value as ExamType)}
          options={EXAM_TYPES.map((examTypeOption) => ({
            value: examTypeOption,
            label: EXAM_TYPE_LABELS[examTypeOption],
          }))}
        />
        <Select
          label="Prova real ou simulado"
          value={examKind}
          onChange={(e) => setExamKind(e.target.value as ExamKind)}
          options={EXAM_KINDS.map((kind) => ({
            value: kind,
            label: EXAM_KIND_LABELS[kind],
          }))}
        />
      </div>
      <FormActions
        pending={pending}
        localError={localError}
        serverError={error}
        onCancel={onCancel}
      />
    </form>
  );
}

// --- QUESTION ----------------------------------------------------------------

let altKeySeq = 0;
const nextAltKey = () => `alt-${altKeySeq++}`;

const QUESTION_FORMATS = ['MULTIPLE_CHOICE', 'ESSAY'] as const;

/** Chave da dispensa da dica de arrastar imagem. */
const DRAG_HINT_KEY = 'question-form:drag-image-hint';

/** Gera ids limpos por posição (a, b, c, …) — o usuário nunca digita ids. */
function alternativeId(index: number): string {
  return index < 26 ? String.fromCharCode(97 + index) : `alt${index + 1}`;
}

function QuestionForm({
  body,
  pending,
  error,
  onSubmit,
  onCancel,
  onDirtyChange,
  buildRef,
  imageUrlsByFileId,
  annulled,
  extraSections,
}: SubFormProps & {
  imageUrlsByFileId: Record<string, string>;
  annulled: boolean;
  extraSections?: ReactNode;
}) {
  const b = asRecord(body);
  /**
   * O texto entra no editor com cada token de imagem já convertido em no. E sai
   * convertido de volta no `build()`. Fora dessas duas bordas, token não existe
   * neste componente — e é por isso que não há mais como o autor ver
   * `{{img:9f2c…}}` no meio do enunciado.
   */
  const toEditor = (raw: unknown) =>
    tokensToEditorHtml(str(raw), imageUrlsByFileId);

  const [format, setFormat] = useState<'MULTIPLE_CHOICE' | 'ESSAY'>(
    b.format === 'ESSAY' ? 'ESSAY' : 'MULTIPLE_CHOICE',
  );
  const [stem, setStem] = useState(() => toEditor(b.stem));
  /**
   * Só da dissertativa, e no mesmo editor do enunciado: a resposta esperada
   * aceita a mesma gramática (texto rico + imagem). Fica em estado mesmo com o
   * formato em múltipla escolha — trocar o formato e voltar não perde o texto —,
   * mas só entra no body quando o formato é `ESSAY`.
   */
  const [expectedAnswer, setExpectedAnswer] = useState(() =>
    toEditor(b.expectedAnswer),
  );
  // Computa alternativas + correta juntas (uma vez, no mount) — compartilham keys.
  const [{ alternatives, correctKey }, setAlts] = useState(() => {
    const rawAlts = Array.isArray(b.alternatives)
      ? (b.alternatives as unknown[]).map((raw) => {
          const a = asRecord(raw);
          return { key: nextAltKey(), text: toEditor(a.text), id: str(a.id) };
        })
      : [];
    const seed = rawAlts.length
      ? rawAlts
      : [
          { key: nextAltKey(), text: '', id: '' },
          { key: nextAltKey(), text: '', id: '' },
        ];
    return {
      alternatives: seed.map((a) => ({ key: a.key, text: a.text })),
      correctKey:
        seed.find((a) => a.id !== '' && a.id === str(b.correctAlternativeId))
          ?.key ?? '',
    };
  });
  /**
   * As três marcações editoriais. `annulled` chega por prop (o card o lê do
   * body do preview) e as outras duas do body cru — mas as três são **estado
   * do form** daqui para frente.
   *
   * `annulled` também é editável pela grade do gabarito, e não há corrida: a
   * grade desabilita o lápis da questão que está aberta no formulário.
   */
  const [appealable, setAppealable] = useState(b.appealable === true);
  const [outdated, setOutdated] = useState(b.outdated === true);
  const [annulledFlag, setAnnulledFlag] = useState(annulled);
  /**
   * A anulada que já chegou sem letra (a importada da Aristo). Só ela pode
   * seguir sem: o admin não cria anulada sem letra (Ruan, 2026-10-19), então a
   * que ele anulou e cuja correta ele apagou continua precisando de uma.
   */
  const [loadedWithoutLetter] = useState(
    () =>
      annulled &&
      b.format === 'MULTIPLE_CHOICE' &&
      typeof b.correctAlternativeId !== 'string',
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  /**
   * Remonta os editores. Mudança que vem de FORA do TipTap (restaurar o
   * original) não aparece sozinha — `content` só vale no mount. Sincronizar
   * `value` a cada render, não: o HTML que o editor normaliza nunca é
   * byte-a-byte igual ao que veio do servidor, e a comparação entraria em laço.
   */
  const [editorEpoch, setEditorEpoch] = useState(0);

  /**
   * Bytes das imagens anexadas nesta edição, indexados por nome. Não é "a lista
   * de anexos" da UI — é só o depósito de bytes; quem diz o que sobe é o
   * documento (`attachedFilenames`). Remover a figura do texto tira o upload
   * junto, sem sincronização nenhuma.
   */
  const [images, setImages] = useState<AttachedImage[]>([]);
  /** Arquivos recusados pelo pré-flight. São os únicos que precisam de UI. */
  const [imageIssues, setImageIssues] = useState<PreflightIssue[]>([]);
  const [converting, setConverting] = useState<string | null>(null);

  // O que foi carregado. `useRef` e não estado: nunca muda, e é o alvo do
  // "Restaurar original".
  // O estado "como foi carregado", para o Restaurar. As marcações entram aqui
  // junto do resto: restaurar pela metade é pior que não restaurar — o form
  // volta o texto, mantém a marcação e continua sujo, dizendo que foi restaurado.
  const loaded = useRef({
    format,
    stem,
    expectedAnswer,
    alternatives,
    correctKey,
    appealable: b.appealable === true,
    outdated: b.outdated === true,
    annulled,
  });

  /**
   * Volta o form ao que veio do banco.
   *
   * Existe ao lado do Desfazer, e não em vez dele, porque resolvem coisas
   * diferentes: Desfazer devolve **um** passo (a imagem apagada sem querer, sem
   * mexer no resto); restaurar joga fora a edição inteira. Sem o segundo, quem
   * quisesse recomeçar tinha de sair da edição — e sair fecha o card.
   *
   * As imagens anexadas nesta sessão vão junto: os nós que as citavam deixaram
   * de existir, e bytes sem nó não sobem (`toImagePayload` filtra pelo texto).
   */
  const restore = () => {
    setFormat(loaded.current.format);
    setStem(loaded.current.stem);
    setExpectedAnswer(loaded.current.expectedAnswer);
    setAlts({
      alternatives: loaded.current.alternatives,
      correctKey: loaded.current.correctKey,
    });
    setAppealable(loaded.current.appealable);
    setOutdated(loaded.current.outdated);
    setAnnulledFlag(loaded.current.annulled);
    setImages([]);
    setImageIssues([]);
    setLocalError(null);
    setEditorEpoch((epoch) => epoch + 1);
  };

  const setAlternatives = (
    updater: (prev: { key: string; text: string }[]) => {
      key: string;
      text: string;
    }[],
  ) =>
    setAlts((prev) => ({ ...prev, alternatives: updater(prev.alternatives) }));

  const setCorrectKey = (key: string) =>
    setAlts((prev) => ({ ...prev, correctKey: key }));

  const setAltText = (key: string, text: string) =>
    setAlternatives((prev) =>
      prev.map((a) => (a.key === key ? { ...a, text } : a)),
    );

  const addAlternative = () =>
    setAlternatives((prev) => [...prev, { key: nextAltKey(), text: '' }]);

  const removeAlternative = (key: string) => {
    setAlternatives((prev) => prev.filter((a) => a.key !== key));
    if (correctKey === key) setCorrectKey('');
  };

  // --- Imagens --------------------------------------------------------------

  /**
   * Escolhe um arquivo, confere os **bytes** (mesmo pré-flight da importação —
   * `accept="image/*"` não impede um AVIF chamado `.png`) e devolve como
   * exibi-lo. `null` = recusado ou cancelado, e aí nada e inserido no texto.
   */
  const pickImage = async (): Promise<PickedImage | null> => {
    const file = await openImageFilePicker();
    if (!file) return null;
    // `positions: []` — aqui não há linha de CSV; o resto da classificação (e o
    // texto da recusa) é exatamente o mesmo da importação.
    const result = await preflightChosenFile(file.name, [], file);
    if ('issue' in result) {
      setImageIssues((prev) => [
        ...prev.filter((entry) => entry.filename !== file.name),
        result.issue,
      ]);
      return null;
    }
    return keepBytes(file.name, result.prepared);
  };

  const keepBytes = async (
    filename: string,
    prepared: PreparedImage,
  ): Promise<PickedImage> => {
    const base64 = await preparedToBase64(prepared);
    const previewUrl = toDataUri(base64, prepared.mime);
    setImages((prev) => {
      // Mesmo nome duas vezes e recusado pelo back ("filenames must be
      // unique") — o último ganha, que é o que "Trocar" espera fazer.
      const byName = new Map(prev.map((image) => [image.filename, image]));
      byName.set(filename, { filename, base64, previewUrl });
      return [...byName.values()];
    });
    setImageIssues((prev) =>
      prev.filter((entry) => entry.filename !== filename),
    );
    return { filename, src: previewUrl };
  };

  /**
   * Converter no browser é auto-correção, não retry: AVIF decodifica nativo, e
   * o admin conserta sem sair da tela. É botão explícito porque alterar os
   * bytes do material que ele mesmo enviou é decisão dele.
   */
  const convert = async (issue: PreflightIssue) => {
    setConverting(issue.filename);
    try {
      const outcome = await convertIssue(issue);
      if ('failure' in outcome) {
        // A pendência continua, com o motivo certo e sem prometer conversão de
        // novo (o `blob` sai junto, e é ele que liga o botão).
        setImageIssues((prev) =>
          prev.map((current) =>
            current.filename === issue.filename
              ? {
                  ...current,
                  reason: outcome.failure.reason,
                  blob: undefined,
                  rejection: outcome.failure.rejection,
                }
              : current,
          ),
        );
        return;
      }
      // O nome não muda: é ele que o texto cita, e o back decide o mime pelos
      // bytes. Convertida, a imagem entra no texto onde o cursor estiver.
      await keepBytes(issue.filename, outcome.prepared);
    } finally {
      setConverting(null);
    }
  };

  // Os textos que vão no body do formato atual: imagem citada só no campo do
  // outro formato não sobe (o back recusa arquivo sem token).
  const texts =
    format === 'ESSAY'
      ? [stem, expectedAnswer]
      : [stem, ...alternatives.map((alt) => alt.text)];
  /**
   * O nó de imagem é `draggable`, mas nada na tela diz isso — e arrastar é a
   * única forma de mudar a figura de lugar sem apagar e reinserir. A dica só
   * aparece quando **há** imagem no texto: ensinar a arrastar uma figura que
   * não existe é a definição de ruído.
   */
  const hasImage = texts.some((html) => html.includes('data-question-image'));

  /**
   * As flags no formato em que o back as grava: chave presente **só quando
   * `true`**. Emitir `outdated: false` funcionaria, mas encheria 80 documentos
   * de `false` e faria desmarcar deixar rastro em vez de devolver o documento
   * ao que era.
   */
  const editorialFlags = {
    ...(appealable ? { appealable: true as const } : {}),
    ...(outdated ? { outdated: true as const } : {}),
  };

  const build = (): BodyFormResult | null => {
    // Sobe só o que o documento cita — o resto nem existe do ponto de vista do
    // back, que recusa arquivo sem token.
    const used = attachedFilenames(texts);
    const payloadImages = toImagePayload(images, used);

    /**
     * "Vazio?" é perguntado na forma **token**, depois de `editorHtmlToTokens`,
     * e não no HTML do editor.
     *
     * Dentro do editor a figura é um NÓ (`<img data-question-image>`), e
     * `richTextIsEmpty` derruba as tags para ver o que sobra de texto — de um
     * campo que só tem imagem não sobra nada, e ele era recusado como vazio.
     * Só que o que é gravado não é o `<img>`: é o token (`{{img:<fileId>}}`,
     * ou `[](imagens/<arquivo>)` no que acabou de ser anexado), que é TEXTO. O
     * backend faz a mesma pergunta sobre esse texto
     * (`content-types/rich-text.ts`) e responde "não está vazio" — ele já
     * aceita enunciado e alternativa só de imagem.
     *
     * Ou seja: a regra era a mesma nos dois lados, feita sobre representações
     * diferentes, e a tela recusava o que o servidor aceitava. Alternativa que
     * é só uma figura é o caso normal em prova de imagem (ECG, lâmina, peça
     * anatômica) — quem precisava disso não tinha saída nenhuma pela interface.
     */
    const stemText = editorHtmlToTokens(stem);
    if (richTextIsEmpty(stemText)) {
      setLocalError('O enunciado é obrigatório.');
      return null;
    }

    if (format === 'ESSAY') {
      setLocalError(null);
      // Vazia, a chave sai: o `PATCH` substitui o body inteiro, então omitir é
      // como apagar a resposta esperada — e o back não grava o campo vazio.
      const expectedAnswerText = editorHtmlToTokens(expectedAnswer);
      return {
        body: {
          format: 'ESSAY',
          stem: sanitizeRichText(stemText),
          ...(richTextIsEmpty(expectedAnswerText)
            ? {}
            : { expectedAnswer: sanitizeRichText(expectedAnswerText) }),
          ...editorialFlags,
        },
        images: payloadImages.length > 0 ? payloadImages : undefined,
      };
    }

    if (alternatives.length < 2) {
      setLocalError('São necessárias ao menos 2 alternativas.');
      return null;
    }
    // Mesma conversão antes de perguntar — ver o comentário do `stemText`.
    const alternativeTexts = alternatives.map((a) =>
      editorHtmlToTokens(a.text),
    );
    if (alternativeTexts.some((text) => richTextIsEmpty(text))) {
      setLocalError(
        'Toda alternativa precisa ter conteúdo — texto, uma imagem, ou os dois.',
      );
      return null;
    }
    const correctIndex = alternatives.findIndex((a) => a.key === correctKey);
    if (correctIndex < 0 && !(annulledFlag && loadedWithoutLetter)) {
      // As letras ficam travadas enquanto "Anulada" está ligada (só se chega
      // aqui anulada apagando a alternativa que era a correta), então a saída
      // é desligá-la.
      setLocalError(
        annulledFlag
          ? 'A questão está anulada e ficou sem alternativa correta guardada. Desligue "Anulada", marque a letra e ligue de novo.'
          : 'Marque a alternativa correta clicando na letra.',
      );
      return null;
    }
    setLocalError(null);

    const built = alternativeTexts.map((text, i) => ({
      id: alternativeId(i),
      text: sanitizeRichText(text),
    }));
    return {
      body: {
        format: 'MULTIPLE_CHOICE',
        stem: sanitizeRichText(stemText),
        alternatives: built,
        ...(correctIndex >= 0
          ? { correctAlternativeId: built[correctIndex]!.id }
          : {}),
        annulled: annulledFlag,
        ...editorialFlags,
      },
      images: payloadImages.length > 0 ? payloadImages : undefined,
    };
  };

  const { handleSubmit, dirty } = useFormBridge({
    // As flags entram no snapshot: fora dele, marcar a caixa não sujaria o
    // form — o Salvar seguiria desabilitado, a guarda de edição não perguntaria
    // nada ao trocar de questão, e a marcação sumiria sem aviso.
    snapshot: JSON.stringify({
      format,
      stem,
      expectedAnswer,
      alternatives,
      correctKey,
      appealable,
      outdated,
      annulledFlag,
    }),
    build,
    onSubmit,
    onDirtyChange,
    buildRef,
  });

  const correctIndex = alternatives.findIndex((a) => a.key === correctKey);
  /**
   * O texto do form **já é** HTML com `<img>` de verdade, porque a imagem virou
   * nó. Então o preview não resolve token nenhum: renderiza o mesmo HTML com o
   * mesmo componente do card de leitura. Uma segunda renderização "parecida" é
   * como preview e resultado final se descolam.
   */
  const previewBody: QuestionBody =
    format === 'ESSAY'
      ? {
          format: 'ESSAY',
          stem,
          // Mesma pergunta do `build()`: o editor vazio ainda devolve `<p></p>`,
          // e o preview desenharia uma caixa de resposta em branco.
          ...(richTextIsEmpty(editorHtmlToTokens(expectedAnswer))
            ? {}
            : { expectedAnswer }),
          ...editorialFlags,
        }
      : {
          format: 'MULTIPLE_CHOICE',
          stem,
          alternatives: alternatives.map((alt, i) => ({
            id: alternativeId(i),
            text: alt.text,
          })),
          ...(correctIndex >= 0
            ? { correctAlternativeId: alternativeId(correctIndex) }
            : {}),
          annulled: annulledFlag,
          ...editorialFlags,
        };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Editar × Preview: o card já é a largura útil da tela, e o preview tem
            de ter a MESMA largura do resultado final para dizer algo sobre como
            a questão vai ficar. Trocar de aba não perde estado (o form continua
            montado) e não conta como salvar. */}
        <div
          className="border-(--color-border) inline-flex overflow-hidden rounded-lg border"
          role="tablist"
        >
          {(['edit', 'preview'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={mode === tab}
              onClick={() => setMode(tab)}
              className={[
                'px-3 py-1 text-xs',
                mode === tab
                  ? 'bg-(--color-accent) text-white'
                  : 'text-(--color-text-secondary) hover:bg-(--color-bg-secondary)',
              ].join(' ')}
            >
              {tab === 'edit' ? 'Editar' : 'Preview'}
            </button>
          ))}
        </div>
        {/* Sem `label`: o próprio valor selecionado ("Múltipla escolha") já diz
            o que o campo é, e uma etiqueta acima somaria uma linha ao card. O
            nome acessível vem do `aria-label`. O wrapper limita a largura —
            o `Select` é `w-full` por dentro. */}
        <div className="w-44">
          <Select
            aria-label="Formato da questão"
            className="py-1.5 text-xs"
            value={format}
            onChange={(e) =>
              setFormat(e.target.value as 'MULTIPLE_CHOICE' | 'ESSAY')
            }
            options={QUESTION_FORMATS.map((f) => ({
              value: f,
              label:
                f === 'MULTIPLE_CHOICE' ? 'Múltipla escolha' : 'Dissertativa',
            }))}
          />
        </div>
      </div>

      {mode === 'preview' ? (
        <div className="border-(--color-border) space-y-3 rounded-lg border p-3">
          {/* As marcações são renderizadas AQUI, e não dentro do
              `QuestionBodyView`: no card em leitura elas já estão no header, e
              renderizá-las nos dois lugares mostrava a mesma marcação duas
              vezes na mesma questão. O preview não tem header — é este bloco
              que responde "como vai ficar?" assim que o interruptor é ligado,
              antes de salvar. */}
          {questionFlags(previewBody).length > 0 && (
            <div className="flex flex-wrap gap-1">
              <QuestionFlagBadges body={previewBody} />
            </div>
          )}
          <QuestionBodyView body={previewBody} />
        </div>
      ) : (
        <>
          {hasImage && (
            <DismissibleHint storageKey={DRAG_HINT_KEY}>
              Para mover uma imagem, <strong>clique e arraste</strong> ela para
              onde quiser — no enunciado ou numa alternativa. Clicando nela uma
              vez, a barra passa a oferecer <strong>Trocar</strong> e{' '}
              <strong>Remover</strong>.
            </DismissibleHint>
          )}

          <RichTextEditor
            key={`stem-${editorEpoch}`}
            label="Enunciado"
            value={stem}
            onChange={setStem}
            onPickImage={pickImage}
          />

          {/* Marcações editoriais, acima das alternativas: as três são do mesmo
              tipo (afirmações sobre a questão) e ficam juntas. "Anulada" só
              aparece em múltipla escolha — "não há alternativa correta a
              considerar" não significa nada numa dissertativa. */}
          <fieldset className="space-y-1.5">
            <legend className={FIELD_TITLE_CLASS}>Marcações</legend>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {format === 'MULTIPLE_CHOICE' && (
                <Switch
                  label="Anulada"
                  hint="A banca anulou — não há alternativa correta a considerar"
                  checked={annulledFlag}
                  onChange={setAnnulledFlag}
                />
              )}
              <Switch
                label="Passível de recurso"
                hint="Cabe recurso contra ela (gabarito duvidoso, enunciado ambíguo)"
                checked={appealable}
                onChange={setAppealable}
              />
              <Switch
                label="Desatualizada"
                hint="A conduta ou diretriz mudou desde que a prova foi aplicada"
                checked={outdated}
                onChange={setOutdated}
              />
            </div>
          </fieldset>

          {format === 'MULTIPLE_CHOICE' && (
            <div className="space-y-1.5">
              <span className={FIELD_TITLE_CLASS}>
                Alternativas{' '}
                {/* A dica não é título: volta ao tom e ao tamanho do texto. */}
                <span className="text-(--color-text-secondary) text-sm font-normal">
                  {annulledFlag
                    ? '— questão anulada: desligue "Anulada" para marcar a alternativa correta'
                    : '— clique na letra para marcar a correta'}
                </span>
              </span>
              {alternatives.map((alt, i) => {
                const letter = alternativeId(i).toUpperCase();
                /**
                 * Anulada não tem alternativa correta: nenhuma letra fica
                 * verde e nenhuma é clicável — é o mesmo que o card em
                 * leitura já faz (`QuestionBodyView` não pinta nada de verde
                 * numa questão anulada), e ter as duas coisas na tela ao mesmo
                 * tempo dizia que a questão é anulada E que a resposta é a C.
                 *
                 * A letra marcada, quando existe, continua **guardada** no
                 * `correctKey` (e vai no `build()`): é ela que volta a valer
                 * se a questão for desanulada.
                 */
                const isCorrect = !annulledFlag && correctKey === alt.key;
                return (
                  <div key={alt.key} className="flex items-center gap-2">
                    {/* A letra É o controle. Um radio ao lado de cada linha
                        somava um alvo de clique e uma coluna de largura para
                        dizer o que a própria letra pode dizer. */}
                    <button
                      type="button"
                      disabled={annulledFlag}
                      onClick={() => setCorrectKey(alt.key)}
                      aria-pressed={isCorrect}
                      title={
                        annulledFlag
                          ? 'Questão anulada — desligue "Anulada" para marcar a alternativa correta'
                          : isCorrect
                            ? `${letter} é a alternativa correta`
                            : `Marcar ${letter} como correta`
                      }
                      className={[
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                        annulledFlag
                          ? 'border-(--color-border) text-(--color-text-secondary) cursor-not-allowed opacity-60'
                          : isCorrect
                            ? 'border-green-500 bg-green-500 text-white'
                            : 'border-(--color-border) text-(--color-text-secondary) hover:border-(--color-accent)',
                      ].join(' ')}
                    >
                      {letter}
                    </button>
                    <div className="min-w-0 flex-1">
                      <RichTextEditor
                        key={`${alt.key}-${editorEpoch}`}
                        variant="compact"
                        value={alt.text}
                        onChange={(html) => setAltText(alt.key, html)}
                        onPickImage={pickImage}
                      />
                    </div>
                    <IconButton
                      label={`Remover a alternativa ${letter}`}
                      icon={<Trash size="sm" />}
                      variant="ghost"
                      disabled={alternatives.length <= 2}
                      onClick={() => removeAlternative(alt.key)}
                    />
                  </div>
                );
              })}
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={addAlternative}
              >
                + Adicionar alternativa
              </Button>
            </div>
          )}

          {/* No lugar das alternativas: é o que a dissertativa tem para dizer
              qual é a resposta. Opcional — vazia, sai do body. */}
          {format === 'ESSAY' && (
            <RichTextEditor
              key={`expected-answer-${editorEpoch}`}
              label="Resposta esperada"
              value={expectedAnswer}
              onChange={setExpectedAnswer}
              onPickImage={pickImage}
            />
          )}

          {/* Não é painel: existe só quando há arquivo recusado, e usa o mesmo
              card da importação para o problema ter uma gramática visual só. */}
          {imageIssues.map((issue) => (
            <ImportIssueCard
              key={issue.filename}
              title={issue.filename}
              badge={
                issue.reason === 'CONVERTIBLE'
                  ? 'converter'
                  : issue.reason === 'TOO_LARGE'
                    ? 'diminuir'
                    : 'trocar'
              }
              message={describeIssue(issue)}
              actions={
                issue.reason === 'CONVERTIBLE' ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="xs"
                    disabled={converting === issue.filename}
                    onClick={() => void convert(issue)}
                  >
                    {converting === issue.filename
                      ? 'Convertendo…'
                      : 'Converter para WebP'}
                  </Button>
                ) : undefined
              }
            />
          ))}
        </>
      )}

      {/* Seções que pertencem à questão mas não ao `body` — classificação e
          comentário oficial — entram AQUI DENTRO do `<form>`, antes das ações.
          É o que põe o "Salvar" no fim do card: com elas depois do form, o
          botão ficava no meio, salvando parte do que estava na tela.

          Elas salvam pelos próprios endpoints (são outras entidades), mas pelo
          mesmo submit — quem orquestra é o card, via refs. */}
      {mode === 'edit' && extraSections}

      <FormActions
        pending={pending}
        localError={localError}
        serverError={error}
        onCancel={onCancel}
        onRestore={dirty ? restore : undefined}
      />
    </form>
  );
}
